import React, { useState, useEffect } from 'react';
import apiClient from '../../../api/client';

interface PathwayResult {
  pathway_id: string;
  pathway_name: string;
  source: string;
  source_type: 'direct' | 'target';
  species: string;
  evidence_code: string | null;
  proteins_hit: string[];
  protein_count_total: number;
  reactome_url: string | null;
}

interface HierarchyItem {
  parent_id: string;
  parent_name: string | null;
  child_id: string;
  child_name: string | null;
}

interface PathwaysTabProps {
  smiles: string;
}

const PathwaysTab: React.FC<PathwaysTabProps> = ({ smiles }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pathways, setPathways] = useState<PathwayResult[]>([]);
  const [hierarchy, setHierarchy] = useState<HierarchyItem[]>([]);
  const [fetched, setFetched] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!smiles || fetched) return;

    const fetchPathways = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get('/enrich/compound-pathways', {
          params: { smiles },
        });
        const data = response.data;
        setPathways(data.pathways || []);
        setHierarchy(data.hierarchy || []);
        setFetched(true);

        // Auto-expand all parents that have children
        const parentIds = new Set<string>((data.hierarchy || []).map((h: HierarchyItem) => h.parent_id));
        setExpandedParents(parentIds);
      } catch (err: any) {
        const detail = err.response?.data?.detail;
        setError(detail || 'Pathway data unavailable — the enrichment service may be temporarily unreachable');
      } finally {
        setLoading(false);
      }
    };

    fetchPathways();
  }, [smiles, fetched]);

  const toggleParent = (parentId: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-6 h-6 border-2 border-t-teal-500 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#14b8a6' }} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Querying Reactome pathways...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
        <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
        <button
          onClick={() => { setFetched(false); setError(null); }}
          className="mt-2 text-xs underline hover:no-underline"
          style={{ color: '#991b1b' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (fetched && pathways.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
        </svg>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No pathway associations found</p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>This compound's targets are not linked to known Reactome pathways</p>
      </div>
    );
  }

  // Build parent-to-children map from hierarchy
  const parentChildMap = new Map<string, string[]>();
  for (const h of hierarchy) {
    const children = parentChildMap.get(h.parent_id) || [];
    children.push(h.child_id);
    parentChildMap.set(h.parent_id, children);
  }

  // Build parent name map
  const parentNameMap = new Map<string, string>();
  for (const h of hierarchy) {
    if (h.parent_name) parentNameMap.set(h.parent_id, h.parent_name);
  }

  // Group pathways by source_type
  const directPathways = pathways.filter(p => p.source_type === 'direct');
  const targetPathways = pathways.filter(p => p.source_type === 'target');

  // Group target pathways by parent (from hierarchy)
  const pathwayById = new Map(pathways.map(p => [p.pathway_id, p]));
  const childToParent = new Map<string, string>();
  for (const h of hierarchy) {
    childToParent.set(h.child_id, h.parent_id);
  }

  // Group by top-level parent
  const groupedByParent = new Map<string, PathwayResult[]>();
  const ungrouped: PathwayResult[] = [];

  for (const p of targetPathways) {
    const parentId = childToParent.get(p.pathway_id);
    if (parentId) {
      const group = groupedByParent.get(parentId) || [];
      group.push(p);
      groupedByParent.set(parentId, group);
    } else {
      ungrouped.push(p);
    }
  }

  const renderPathwayRow = (pathway: PathwayResult, indent: number = 0) => (
    <div
      key={pathway.pathway_id}
      className="p-2.5 rounded"
      style={{
        marginLeft: indent * 20,
        borderLeft: '3px solid #0d9488',
        backgroundColor: indent > 0 ? '#f0fdfa' : 'var(--card-bg)',
        border: '1px solid var(--card-border)',
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium leading-tight" style={{ color: 'var(--text-primary)' }}>
          {pathway.pathway_name}
        </span>
        {/* Protein count badge */}
        {pathway.protein_count_total > 0 && (
          <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
            {pathway.proteins_hit.length} of {pathway.protein_count_total}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {/* Reactome link */}
        {pathway.reactome_url ? (
          <a
            href={pathway.reactome_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-600 hover:text-teal-800 hover:underline flex items-center gap-0.5"
            style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}
            onClick={(e) => e.stopPropagation()}
          >
            {pathway.pathway_id}
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        ) : (
          <span
            style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono, monospace)', fontSize: 11 }}
          >
            {pathway.pathway_id}
          </span>
        )}
        {pathway.source && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{pathway.source}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3 max-h-[500px] overflow-y-auto">
      <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
        Pathways ({pathways.length})
      </h4>

      {/* Direct pathways */}
      {directPathways.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Direct Pathways ({directPathways.length})
          </h5>
          <div className="space-y-1.5">
            {directPathways.map(p => renderPathwayRow(p))}
          </div>
        </div>
      )}

      {/* Target-linked pathways grouped by parent */}
      {(groupedByParent.size > 0 || ungrouped.length > 0) && (
        <div>
          <h5 className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Target-Linked Pathways ({targetPathways.length})
          </h5>
          <div className="space-y-1.5">
            {/* Grouped pathways */}
            {Array.from(groupedByParent.entries()).map(([parentId, children]) => (
              <div key={parentId}>
                {/* Parent header */}
                <button
                  onClick={() => toggleParent(parentId)}
                  className="w-full text-left flex items-center gap-1.5 py-1.5 px-2 rounded transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
                    style={{ color: 'var(--text-muted)', transform: expandedParents.has(parentId) ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {parentNameMap.get(parentId) || parentId}
                  </span>
                  <span className="text-xs ml-1" style={{ color: 'var(--text-muted)' }}>({children.length})</span>
                </button>
                {/* Children */}
                {expandedParents.has(parentId) && (
                  <div className="space-y-1 mt-1">
                    {children.map(p => renderPathwayRow(p, 1))}
                  </div>
                )}
              </div>
            ))}

            {/* Ungrouped pathways */}
            {ungrouped.map(p => renderPathwayRow(p))}
          </div>
        </div>
      )}

      {/* Fallback: if no grouping worked, show flat list */}
      {directPathways.length === 0 && groupedByParent.size === 0 && ungrouped.length === 0 && pathways.length > 0 && (
        <div className="space-y-1.5">
          {pathways.map(p => renderPathwayRow(p))}
        </div>
      )}
    </div>
  );
};

export default PathwaysTab;
